import React from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface EHRHeaderProps {
  patient: {
    name: string;
    birthDate: string;
    age: number;
    gender: string;
    birthPlace: string;
    address: string;
    status: string;
    initials: string;
  };
  onExportPdf?: () => void;
  isExportingPdf?: boolean;
  onPrint?: () => void;
  isPrinting?: boolean;
}

export function EHRHeader({
  patient,
  onExportPdf,
  isExportingPdf,
  onPrint,
  isPrinting,
}: EHRHeaderProps) {
  const handleExportPdf = onExportPdf || onPrint;
  const isGeneratingPdf = isExportingPdf || isPrinting || false;

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-100 dark:border-neutral-800 shadow-sm mb-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center text-teal-700 dark:text-teal-400 font-bold text-xl">
            {patient.initials}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              {patient.name}
            </h1>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-400">
              {patient.status}
            </span>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={handleExportPdf}
          disabled={isGeneratingPdf || !handleExportPdf}
          className="flex items-center gap-2 border-teal-100 text-teal-700 hover:bg-teal-50"
        >
          {isGeneratingPdf ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {isGeneratingPdf ? 'Gerando PDF...' : 'Exportar PDF'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div>
          <p className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-wider mb-1">Nascimento</p>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{patient.birthDate} ({patient.age} anos)</p>
        </div>

        <div>
          <p className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-wider mb-1">Sexo</p>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{patient.gender}</p>
        </div>

        <div>
          <p className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-wider mb-1">Nacionalidade/Origem</p>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{patient.birthPlace}</p>
        </div>

        <div>
          <p className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-wider mb-1">Endereço</p>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 leading-tight">{patient.address}</p>
        </div>
      </div>
    </div>
  );
}
